'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  leading?: React.ReactNode;
}

type SelectMenuPosition = {
  left: number;
  width: number;
  maxHeight: number;
  top: number;
};

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'marketplace';
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className,
  disabled = false,
  variant = 'default',
}: SelectProps) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const listboxId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [menuPosition, setMenuPosition] =
    React.useState<SelectMenuPosition | null>(null);
  const selectedOption = options.find((option) => option.value === value);

  const updateMenuPosition = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gutter = 10;
    const sideOffset = 6;
    const minWidth = Math.min(180, Math.max(120, viewportWidth - gutter * 2));
    const width = Math.min(
      Math.max(rect.width, minWidth),
      Math.max(minWidth, viewportWidth - gutter * 2)
    );
    const left = Math.min(
      Math.max(gutter, rect.left),
      Math.max(gutter, viewportWidth - width - gutter)
    );
    const bottomSpace = viewportHeight - rect.bottom - gutter;
    const topSpace = rect.top - gutter;
    const shouldOpenUp = bottomSpace < 180 && topSpace > bottomSpace;
    const availableSpace = shouldOpenUp ? topSpace : bottomSpace;
    const maxHeight = Math.min(288, Math.max(144, availableSpace - sideOffset));
    const top = shouldOpenUp
      ? Math.max(gutter, rect.top - maxHeight - sideOffset)
      : Math.min(rect.bottom + sideOffset, viewportHeight - maxHeight - gutter);

    setMenuPosition({
      left,
      width,
      maxHeight,
      top,
    });
  }, []);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useLayoutEffect(() => {
    if (open) {
      updateMenuPosition();
    }
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  React.useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        className={cn(
          'flex h-11 w-full items-center justify-between gap-3 rounded-2xl border border-input bg-white px-4 py-2 text-sm text-ink-950 shadow-sm ring-offset-background transition hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
          className
        )}
        onMouseDown={(event) => event.preventDefault()}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => {
          if (disabled) return;

          if (!open) {
            updateMenuPosition();
          }

          setOpen((current) => !current);
        }}
      >
        <span className="flex min-w-0 flex-1 items-center gap-3">
          {selectedOption?.leading && (
            <span aria-hidden="true" className="shrink-0">
              {selectedOption.leading}
            </span>
          )}
          <span
            className={cn(
              'min-w-0 flex-1 text-left',
              !selectedOption && 'text-muted-foreground'
            )}
          >
            <span className="block truncate">
              {selectedOption?.label ?? placeholder}
            </span>
            {selectedOption?.description && (
              <span className="mt-0.5 block truncate text-[11px] font-normal opacity-50">
                {selectedOption.description}
              </span>
            )}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 opacity-50 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {mounted && open && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              id={listboxId}
              role="listbox"
              className={cn(
                'fixed z-[10050] overflow-y-auto rounded-2xl border p-1 shadow-lift',
                variant === 'marketplace'
                  ? 'border-white/15 bg-[#180a2b]/95 text-white shadow-[0_24px_70px_rgba(6,2,20,0.55)] backdrop-blur-2xl'
                  : 'border-black/10 bg-white text-foreground'
              )}
              style={{
                left: menuPosition.left,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
                top: menuPosition.top,
                zIndex: 10050,
              }}
              onMouseDown={(event) => event.preventDefault()}
              onPointerDownCapture={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onWheel={(event) => event.stopPropagation()}
            >
              {options.map((option) => {
                const isSelected = option.value === value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      'relative flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-xl py-2 pl-8 pr-3 text-left text-sm outline-none transition-colors',
                      variant === 'marketplace'
                        ? 'text-white/75 hover:bg-white/10 hover:text-white focus-visible:bg-white/10'
                        : 'hover:bg-muted focus-visible:bg-muted',
                      isSelected && (
                        variant === 'marketplace'
                          ? 'bg-brand-500/20 text-white'
                          : 'bg-muted text-foreground'
                      )
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <span className={cn(
                      'absolute left-2 flex h-3.5 w-3.5 items-center justify-center',
                      variant === 'marketplace' ? 'text-coral-200' : 'text-brand-700'
                    )}>
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    {option.leading && (
                      <span aria-hidden="true" className="shrink-0">
                        {option.leading}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.description && (
                        <span className="mt-0.5 block truncate text-[11px] font-normal opacity-50">
                          {option.description}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
